import React, { useState, useRef } from "react";

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Upload, Download, FileSpreadsheet, CheckCircle, XCircle,
  AlertTriangle, Loader2, RefreshCw, SkipForward
} from "lucide-react";

// ── Reusable file drop zone ────────────────────────────────────────────────

const DropZone = ({ onFile, accept = ".xlsx", loading, label }) => {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);
  const [filename, setFilename] = useState("");

  const handleFile = (f) => {
    if (!f) return;
    setFilename(f.name);
    onFile(f);
  };

  return (
    <div
      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
        dragging ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-blue-300 bg-slate-50"
      }`}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
      onClick={() => inputRef.current?.click()}
      data-testid="file-dropzone"
    >
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={e => handleFile(e.target.files[0])} />
      {loading ? (
        <div className="flex flex-col items-center gap-2 text-blue-600">
          <Loader2 className="animate-spin w-8 h-8" />
          <p className="text-sm font-medium">Parsing file...</p>
        </div>
      ) : filename ? (
        <div className="flex flex-col items-center gap-2">
          <FileSpreadsheet size={28} className="text-blue-600" />
          <p className="text-sm font-semibold text-slate-900">{filename}</p>
          <p className="text-xs text-slate-500">Click or drop to replace</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <Upload size={28} className="text-slate-400" />
          <p className="text-sm font-semibold text-slate-700">{label}</p>
          <p className="text-xs text-slate-400">Drag & drop or click to browse · .xlsx only</p>
        </div>
      )}
    </div>
  );
};

// ── Row preview table ──────────────────────────────────────────────────────

const PreviewTable = ({ rows, type }) => {
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll ? rows : rows.slice(0, 10);

  const appCols = ["title", "function_id", "subcategory_name", "is_premium", "recommended_tools"];
  const userCols = ["name", "email", "subscription_plan", "company", "department"];
  const cols = type === "apps" ? appCols : userCols;

  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-600 w-12">Row</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-600 w-20">Status</th>
              {cols.map(c => (
                <th key={c} className="text-left px-3 py-2.5 font-semibold text-slate-600 capitalize">
                  {c.replace(/_/g, " ")}
                </th>
              ))}
              <th className="text-left px-3 py-2.5 font-semibold text-slate-600">Errors</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {displayed.map(row => {
              const isDupe = row.is_db_duplicate;
              const isInvalid = !row.is_valid || isDupe;
              return (
                <tr
                  key={row.row_num}
                  className={isInvalid ? "bg-red-50" : "bg-white hover:bg-slate-50"}
                  data-testid={`preview-row-${row.row_num}`}
                >
                  <td className="px-3 py-2.5 text-slate-500">{row.row_num}</td>
                  <td className="px-3 py-2.5">
                    {isDupe ? (
                      <Badge className="text-xs border-0 bg-amber-100 text-amber-700">Duplicate</Badge>
                    ) : row.is_valid ? (
                      <Badge className="text-xs border-0 bg-emerald-100 text-emerald-700">Valid</Badge>
                    ) : (
                      <Badge className="text-xs border-0 bg-red-100 text-red-700">Invalid</Badge>
                    )}
                  </td>
                  {cols.map(c => (
                    <td key={c} className="px-3 py-2.5 text-slate-700 max-w-xs truncate">
                      {c === "recommended_tools" && Array.isArray(row.data?.[c])
                        ? row.data[c].join(", ")
                        : c === "is_premium"
                        ? row.data?.[c] ? "Premium" : "Free"
                        : String(row.data?.[c] ?? "")}
                    </td>
                  ))}
                  <td className="px-3 py-2.5 text-red-600 text-xs max-w-xs">
                    {row.errors?.join("; ") || (isDupe ? "Email already exists in system" : "")}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length > 10 && (
        <button
          onClick={() => setShowAll(s => !s)}
          className="mt-2 text-xs text-blue-600 hover:underline flex items-center gap-1"
        >
          {showAll ? "Show less" : `Show all ${rows.length} rows`}
        </button>
      )}
    </div>
  );
};

// ── Summary banner ────────────────────────────────────────────────────────

const SummaryBanner = ({ total, valid, invalid, dupes }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="preview-summary">
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
      <p className="text-2xl font-bold text-slate-900">{total}</p>
      <p className="text-xs text-slate-500 mt-0.5">Total Rows</p>
    </div>
    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-center">
      <p className="text-2xl font-bold text-emerald-700">{valid}</p>
      <p className="text-xs text-emerald-600 mt-0.5">Ready to Import</p>
    </div>
    <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
      <p className="text-2xl font-bold text-red-600">{invalid}</p>
      <p className="text-xs text-red-500 mt-0.5">Invalid Rows</p>
    </div>
    {dupes !== undefined && (
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-center">
        <p className="text-2xl font-bold text-amber-600">{dupes}</p>
        <p className="text-xs text-amber-600 mt-0.5">Duplicates</p>
      </div>
    )}
  </div>
);

// ── Result banner after import ────────────────────────────────────────────

const ResultBanner = ({ result, onReset }) => (
  <div className="space-y-4">
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle size={18} className="text-emerald-600" />
        <h3 className="font-semibold text-emerald-900">Import Complete</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="text-center"><p className="text-xl font-bold text-emerald-700">{result.imported ?? 0}</p><p className="text-xs text-slate-500">Imported</p></div>
        {result.updated > 0 && <div className="text-center"><p className="text-xl font-bold text-blue-600">{result.updated}</p><p className="text-xs text-slate-500">Updated</p></div>}
        {result.skipped > 0 && <div className="text-center"><p className="text-xl font-bold text-amber-600">{result.skipped}</p><p className="text-xs text-slate-500">Skipped</p></div>}
        {result.failed_count > 0 && <div className="text-center"><p className="text-xl font-bold text-red-600">{result.failed_count}</p><p className="text-xs text-slate-500">Failed</p></div>}
      </div>
    </div>
    <Button variant="outline" onClick={onReset} className="flex items-center gap-2">
      <RefreshCw size={14} /> Upload Another File
    </Button>
  </div>
);

// ── Apps uploader ─────────────────────────────────────────────────────────

const AppsBulkUpload = ({ API }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleFile = async (file) => {
    setError(""); setPreview(null); setResult(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/bulk-upload/preview/applications`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to parse file. Make sure it is a valid .xlsx file.");
    } finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!preview) return;
    const validRows = preview.rows.filter(r => r.is_valid);
    if (validRows.length === 0) { setError("No valid rows to import."); return; }
    setImporting(true); setError("");
    try {
      const res = await axios.post(`${API}/admin/bulk-upload/import/applications`,
        { rows: validRows }, { withCredentials: true });
      setResult(res.data);
      setPreview(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Import failed.");
    } finally { setImporting(false); }
  };

  const handleDownloadErrors = async () => {
    const invalidRows = (preview?.rows || []).filter(r => !r.is_valid);
    try {
      const res = await axios.post(`${API}/admin/bulk-upload/error-report`,
        { rows: invalidRows }, { withCredentials: true });
      const a = document.createElement("a");
      a.href = `${API}/admin/bulk-upload/error-report/${res.data.key}`;
      a.download = "error_report.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch { setError("Failed to download error report."); }
  };

  const handleDownloadTemplate = () => {
    const a = document.createElement("a");
    a.href = `${API}/admin/bulk-upload/template/applications`;
    a.download = "applications_template.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const reset = () => { setPreview(null); setResult(null); setError(""); };
  const validRows = preview?.rows?.filter(r => r.is_valid) || [];
  const invalidRows = preview?.rows?.filter(r => !r.is_valid) || [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Bulk Upload Applications</h3>
          <p className="text-sm text-slate-500 mt-0.5">Import multiple applications at once from an Excel file.</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="flex items-center gap-1.5" data-testid="download-app-template-btn">
          <Download size={14} /> Download Template
        </Button>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      {result ? (
        <ResultBanner result={result} onReset={reset} />
      ) : preview ? (
        <div className="space-y-5">
          <SummaryBanner total={preview.total} valid={preview.valid_count} invalid={preview.invalid_count} />
          <PreviewTable rows={preview.rows} type="apps" />
          <div className="flex flex-wrap gap-3 pt-2 border-t border-slate-100">
            {validRows.length > 0 && (
              <Button
                onClick={handleImport}
                disabled={importing}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="confirm-import-apps-btn"
              >
                {importing ? <><Loader2 className="animate-spin w-4 h-4 mr-1.5" /> Importing...</> : <><CheckCircle size={14} className="mr-1.5" /> Import {validRows.length} Valid Applications</>}
              </Button>
            )}
            {invalidRows.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleDownloadErrors} className="flex items-center gap-1.5" data-testid="download-errors-btn">
                <Download size={13} /> Download Error Report ({invalidRows.length} rows)
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={reset}>Start Over</Button>
          </div>
        </div>
      ) : (
        <DropZone onFile={handleFile} loading={loading} label="Drop your Applications Excel file here" />
      )}

      {/* Field reference */}
      <details className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
        <summary className="px-4 py-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-100">
          Required & Optional Fields Reference
        </summary>
        <div className="px-4 pb-4 pt-2 grid sm:grid-cols-2 gap-1.5 text-xs text-slate-600">
          {[
            ["Title", "Required"],["Function", "Required — procurement/finance/marketing/hr/sales/operations/general"],
            ["Subcategory","Optional — must match existing subcategory name"],
            ["Business Context","Required"],["When to Use","Required"],["Input Required","Required"],
            ["Main Prompt","Required"],["ChatGPT Prompt","Optional — tool variation"],
            ["Claude Prompt","Optional — tool variation"],["Gemini Prompt","Optional — tool variation"],
            ["Expected Output","Required"],["Example Output","Optional"],
            ["Pro Tips","Optional — separate with |"],
            ["Recommended AI Tools","Optional — ChatGPT|Claude|Gemini|Microsoft Copilot|Perplexity"],
            ["Tags","Optional — lowercase, separate with |"],
            ["Access Type","Required — free or premium"],
            ["Credit Cost","Optional — number, default 5 for premium"],
            ["Author Type","Optional — platform (default) or contributor"],
          ].map(([field, note]) => (
            <div key={field} className="flex gap-2">
              <span className="font-medium text-slate-900 shrink-0 w-36">{field}</span>
              <span className="text-slate-500">{note}</span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
};

// ── Enterprise users uploader ─────────────────────────────────────────────

const UsersBulkUpload = ({ API }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [onDuplicate, setOnDuplicate] = useState("skip");

  const handleFile = async (file) => {
    setError(""); setPreview(null); setResult(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/bulk-upload/preview/enterprise-users`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to parse file.");
    } finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!preview) return;
    const validRows = preview.rows.filter(r => r.is_valid);
    if (validRows.length === 0 && preview.rows.filter(r => r.is_db_duplicate).length === 0) {
      setError("No rows to import."); return;
    }
    // Include valid rows + duplicates if update mode
    const rowsToSend = onDuplicate === "update"
      ? preview.rows.filter(r => r.is_valid || r.is_db_duplicate)
      : preview.rows.filter(r => r.is_valid);
    setImporting(true); setError("");
    try {
      const res = await axios.post(`${API}/admin/bulk-upload/import/enterprise-users`,
        { rows: rowsToSend, on_duplicate: onDuplicate }, { withCredentials: true });
      setResult(res.data);
      setPreview(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Import failed.");
    } finally { setImporting(false); }
  };

  const handleDownloadErrors = async () => {
    const invalidRows = (preview?.rows || []).filter(r => !r.is_valid && !r.is_db_duplicate);
    try {
      const res = await axios.post(`${API}/admin/bulk-upload/error-report`,
        { rows: invalidRows }, { withCredentials: true });
      const a = document.createElement("a");
      a.href = `${API}/admin/bulk-upload/error-report/${res.data.key}`;
      a.download = "user_error_report.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch { setError("Failed to download error report."); }
  };

  const handleDownloadTemplate = () => {
    const a = document.createElement("a");
    a.href = `${API}/admin/bulk-upload/template/enterprise-users`;
    a.download = "enterprise_users_template.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const reset = () => { setPreview(null); setResult(null); setError(""); };
  const validRows = preview?.rows?.filter(r => r.is_valid && !r.is_db_duplicate) || [];
  const dupeRows = preview?.rows?.filter(r => r.is_db_duplicate) || [];
  const invalidRows = preview?.rows?.filter(r => !r.is_valid && !r.is_db_duplicate) || [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Bulk Upload Enterprise Users</h3>
          <p className="text-sm text-slate-500 mt-0.5">Import and provision multiple users at once from an Excel file.</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="flex items-center gap-1.5" data-testid="download-user-template-btn">
          <Download size={14} /> Download Template
        </Button>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      {result ? (
        <ResultBanner result={result} onReset={reset} />
      ) : preview ? (
        <div className="space-y-5">
          <SummaryBanner total={preview.total} valid={preview.valid_count} invalid={preview.invalid_count} dupes={preview.duplicate_count} />

          {/* Duplicate handling */}
          {dupeRows.length > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-start gap-2 flex-1">
                <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-900">{dupeRows.length} duplicate email{dupeRows.length > 1 ? "s" : ""} found</p>
                  <p className="text-xs text-amber-700 mt-0.5">These users already exist in the system. Choose how to handle them:</p>
                </div>
              </div>
              <Select value={onDuplicate} onValueChange={setOnDuplicate}>
                <SelectTrigger className="w-44 bg-white" data-testid="duplicate-action-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip duplicates</SelectItem>
                  <SelectItem value="update">Update existing users</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <PreviewTable rows={preview.rows} type="users" />

          <div className="flex flex-wrap gap-3 pt-2 border-t border-slate-100">
            {(validRows.length > 0 || (dupeRows.length > 0 && onDuplicate === "update")) && (
              <Button
                onClick={handleImport}
                disabled={importing}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="confirm-import-users-btn"
              >
                {importing ? <><Loader2 className="animate-spin w-4 h-4 mr-1.5" /> Importing...</> : (
                  <><CheckCircle size={14} className="mr-1.5" />
                    Import {validRows.length} {dupeRows.length > 0 && onDuplicate === "update" ? `+ ${onDuplicate === "update" ? dupeRows.length : 0} updates` : ""}
                  </>
                )}
              </Button>
            )}
            {invalidRows.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleDownloadErrors} className="flex items-center gap-1.5" data-testid="download-user-errors-btn">
                <Download size={13} /> Error Report ({invalidRows.length} rows)
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={reset}>Start Over</Button>
          </div>
        </div>
      ) : (
        <DropZone onFile={handleFile} loading={loading} label="Drop your Enterprise Users Excel file here" />
      )}

      {/* Field reference */}
      <details className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
        <summary className="px-4 py-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-100">
          Required & Optional Fields Reference
        </summary>
        <div className="px-4 pb-4 pt-2 grid sm:grid-cols-2 gap-1.5 text-xs text-slate-600">
          {[
            ["First Name","Required"],["Last Name","Required"],["Email","Required — must be unique"],
            ["Company Name","Optional"],["Department","Optional"],["Job Title","Optional"],
            ["Plan","Required — free, single, multi, full"],
            ["Credits","Optional — overrides plan default"],
            ["Account Status","Optional — active (default) or inactive"],
          ].map(([field, note]) => (
            <div key={field} className="flex gap-2">
              <span className="font-medium text-slate-900 shrink-0 w-32">{field}</span>
              <span className="text-slate-500">{note}</span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
};

// ── Main export ───────────────────────────────────────────────────────────

const BulkUploadSection = ({ API }) => (
  <div className="space-y-4">
    <div>
      <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-1">
        <Upload size={15} className="text-blue-600" /> Bulk Upload
      </h2>
      <p className="text-sm text-slate-500">Upload large sets of data using Excel files. Preview, validate, and import in one flow.</p>
    </div>
    <Tabs defaultValue="applications">
      <TabsList className="mb-5">
        <TabsTrigger value="applications" data-testid="bulk-apps-tab">Applications</TabsTrigger>
        <TabsTrigger value="enterprise-users" data-testid="bulk-users-tab">Enterprise Users</TabsTrigger>
      </TabsList>
      <TabsContent value="applications">
        <AppsBulkUpload API={API} />
      </TabsContent>
      <TabsContent value="enterprise-users">
        <UsersBulkUpload API={API} />
      </TabsContent>
    </Tabs>
  </div>
);

export default BulkUploadSection;
